import { getAccessProfileQueryOptions } from "@packages/api-client/hooks/authorizationController";
import {
	getAnimalQueryOptions,
	listAnimalsByOrganizationQueryOptions,
	listHerdsByOrganizationQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import type { UpdateAnimalMutationRequest } from "@packages/api-client/types/livestockController";
import { Action, type UserAccessProfile } from "@packages/shared-types";
import type {
	LivestockAnimal,
	LivestockHerdWithStats,
} from "@packages/shared-types/livestock";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUpdateAnimal } from "@/api/livestock";
import { PageHeader } from "@/components/PageHeader";
import { FormSkeleton } from "@/components/ui/skeleton";
import { hasPermission } from "@/hooks/useAccessControl";
import { ensureQueryDataSurvivingCancel } from "@/lib/ensureQueryDataSurvivingCancel";
import { ForbiddenError } from "@/lib/forbiddenError";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { AnimalForm } from "../-components/AnimalForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/animals/$animalId/edit",
)({
	// Edits are conflict-prone and never offline (roadmap rule 2) — unlike the
	// detail view, this can't become `ready` via the prepared dataset.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		const profile = await ensureQueryDataSurvivingCancel(
			context.queryClient,
			getAccessProfileQueryOptions({ organizationId: params.orgId }),
		);
		if (!hasPermission(profile as UserAccessProfile, Action.MANAGE_LIVESTOCK)) {
			throw new ForbiddenError("Access denied to animal edit page");
		}
		await Promise.all([
			context.queryClient.ensureQueryData(
				getAnimalQueryOptions(params.orgId, params.animalId),
			),
			context.queryClient.ensureQueryData(
				listHerdsByOrganizationQueryOptions(params.orgId, {
					establishmentId: params.establishmentId,
				}),
			),
			context.queryClient.ensureQueryData(
				listAnimalsByOrganizationQueryOptions(params.orgId, {
					establishmentId: params.establishmentId,
					limit: "1000",
				}),
			),
		]);
	},
	component: EditAnimalPage,
});

function EditAnimalPage() {
	const { orgId, establishmentId, animalId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data: animal, isLoading: animalLoading } = useQuery(
		getAnimalQueryOptions(orgId, animalId),
	);

	const { data: herdsData } = useSuspenseQuery(
		listHerdsByOrganizationQueryOptions(orgId, { establishmentId }),
	);

	const { data: animalsData } = useSuspenseQuery(
		listAnimalsByOrganizationQueryOptions(orgId, {
			establishmentId,
			limit: "1000",
		}),
	);

	const updateMutation = useUpdateAnimal();

	const handleSubmit = (formData: UpdateAnimalMutationRequest) =>
		updateMutation.mutateAsync(
			{ organizationId: orgId, animalId, data: formData },
			{
				onSuccess: () => {
					navigate({
						to: "/app/org/$orgId/livestock/$establishmentId/animals/$animalId",
						params: { orgId, establishmentId, animalId },
					});
				},
			},
		);

	if (animalLoading) {
		return <FormSkeleton />;
	}

	if (!animal) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("livestock.animal.notFound")}</p>
			</div>
		);
	}

	const animalOptions = (animalsData?.data ?? []).map((a) => ({
		id: a.id,
		tagId: a.tagId,
		sex: a.sex,
		status: a.status,
		herdId: a.herdId,
	}));

	return (
		// On a phone the title row sits flush under the app's sticky farm bar:
		// `-mt-3` cancels `<main>`'s own `p-3` top gutter so the 44px back/title
		// row is the only thing between that bar and the form card.
		<div className="-mt-3 flex flex-col gap-2 md:mt-0 md:gap-6">
			<PageHeader title={t("livestock.animal.edit.title")} showBackButton />
			<AnimalForm
				animal={animal as LivestockAnimal}
				establishmentId={establishmentId}
				orgId={orgId}
				herds={(herdsData?.data ?? []) as LivestockHerdWithStats[]}
				animals={animalOptions}
				onSubmit={handleSubmit}
				isSubmitting={updateMutation.isPending}
				onCancel={() =>
					navigate({
						to: "/app/org/$orgId/livestock/$establishmentId/animals/$animalId",
						params: { orgId, establishmentId, animalId },
					})
				}
			/>
		</div>
	);
}
